import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { logger } from '@/lib/telemetry/logger';

// ── Theme variable definitions ──────────────────────────────────────────────

export interface ThemeVariable {
  varName: string;
  label: string;
  group: string;
}

export const THEME_VARIABLE_GROUPS: ReadonlyArray<string> = [
  'Backgrounds',
  'Text',
  'Primary',
  'Semantic',
  'Borders',
] as const;

export const THEME_VARIABLES: ReadonlyArray<ThemeVariable> = [
  // Backgrounds
  { varName: '--background', label: 'Background', group: 'Backgrounds' },
  { varName: '--card', label: 'Card', group: 'Backgrounds' },
  { varName: '--popover', label: 'Popover', group: 'Backgrounds' },
  { varName: '--secondary', label: 'Secondary', group: 'Backgrounds' },
  { varName: '--muted', label: 'Muted', group: 'Backgrounds' },
  { varName: '--accent', label: 'Accent', group: 'Backgrounds' },
  // Text
  { varName: '--foreground', label: 'Foreground', group: 'Text' },
  { varName: '--card-foreground', label: 'Card Foreground', group: 'Text' },
  { varName: '--popover-foreground', label: 'Popover Foreground', group: 'Text' },
  { varName: '--secondary-foreground', label: 'Secondary Foreground', group: 'Text' },
  { varName: '--muted-foreground', label: 'Muted Foreground', group: 'Text' },
  { varName: '--accent-foreground', label: 'Accent Foreground', group: 'Text' },
  { varName: '--primary-foreground', label: 'Primary Foreground', group: 'Text' },
  // Primary
  { varName: '--primary', label: 'Primary', group: 'Primary' },
  // Semantic
  { varName: '--destructive', label: 'Destructive', group: 'Semantic' },
  { varName: '--destructive-foreground', label: 'Destructive Foreground', group: 'Semantic' },
  { varName: '--success', label: 'Success', group: 'Semantic' },
  { varName: '--success-foreground', label: 'Success Foreground', group: 'Semantic' },
  // Borders
  { varName: '--border', label: 'Border', group: 'Borders' },
  { varName: '--input', label: 'Input Border', group: 'Borders' },
  { varName: '--ring', label: 'Ring', group: 'Borders' },
] as const;

// ── Export/Import types ─────────────────────────────────────────────────────

export interface ThemeExport {
  lightOverrides: Record<string, string>;
  darkOverrides: Record<string, string>;
  logoDataUrl: string | null;
}

// ── Store ───────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'morfi:debug-theme:v1';
const LEGACY_STORAGE_KEY = 'morfi:debug-theme';

interface ThemeOverrideState {
  lightOverrides: Record<string, string>;
  darkOverrides: Record<string, string>;
  logoDataUrl: string | null;
  setOverride: (mode: 'light' | 'dark', varName: string, value: string) => void;
  resetVariable: (mode: 'light' | 'dark', varName: string) => void;
  resetAll: () => void;
  importTheme: (config: ThemeExport) => void;
  exportTheme: () => ThemeExport;
  setLogo: (dataUrl: string | null) => void;
}

function loadPersisted(): Pick<
  ThemeOverrideState,
  'lightOverrides' | 'darkOverrides' | 'logoDataUrl'
> {
  const empty = { lightOverrides: {}, darkOverrides: {}, logoDataUrl: null };
  if (typeof window === 'undefined') return empty;
  let fromLegacy = false;
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      fromLegacy = raw !== null;
    }
    if (!raw) return empty;
    // ALLOW-UNKNOWN: JSON.parse returns unknown; validated by structural checks below
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return empty;
    const obj = parsed as Record<string, unknown>;
    const lightVal = obj.lightOverrides;
    const darkVal = obj.darkOverrides;
    const logoVal = obj.logoDataUrl;
    const persisted = {
      lightOverrides:
        typeof lightVal === 'object' && lightVal !== null
          ? (lightVal as Record<string, string>)
          : {},
      darkOverrides:
        typeof darkVal === 'object' && darkVal !== null ? (darkVal as Record<string, string>) : {},
      logoDataUrl: typeof logoVal === 'string' ? logoVal : null,
    };
    if (fromLegacy) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch (err: unknown) {
        logger.warn('Failed to migrate theme overrides in localStorage', {
          error: err instanceof Error ? err.message.slice(0, 200) : String(err),
        });
      }
    }
    return persisted;
  } catch (err: unknown) {
    logger.warn('Failed to parse theme overrides from localStorage', {
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
    return empty;
  }
}

function persist(
  state: Pick<ThemeOverrideState, 'lightOverrides' | 'darkOverrides' | 'logoDataUrl'>,
): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      lightOverrides: state.lightOverrides,
      darkOverrides: state.darkOverrides,
      logoDataUrl: state.logoDataUrl,
    }),
  );
}

export const useThemeOverrideStore: UseBoundStore<StoreApi<ThemeOverrideState>> =
  createTrackedStore<ThemeOverrideState>('useThemeOverrideStore')((set, get) => ({
    ...loadPersisted(),

    setOverride: (mode: 'light' | 'dark', varName: string, value: string): void => {
      const key = mode === 'light' ? 'lightOverrides' : 'darkOverrides';
      const next = { ...get()[key], [varName]: value };
      set({ [key]: next });
      persist({ ...get(), [key]: next });
    },

    resetVariable: (mode: 'light' | 'dark', varName: string): void => {
      const key = mode === 'light' ? 'lightOverrides' : 'darkOverrides';
      const { [varName]: _, ...rest } = get()[key];
      set({ [key]: rest });
      persist({ ...get(), [key]: rest });
    },

    resetAll: (): void => {
      set({ lightOverrides: {}, darkOverrides: {}, logoDataUrl: null });
      persist({ lightOverrides: {}, darkOverrides: {}, logoDataUrl: null });
    },

    importTheme: (config: ThemeExport): void => {
      set({
        lightOverrides: config.lightOverrides,
        darkOverrides: config.darkOverrides,
        logoDataUrl: config.logoDataUrl,
      });
      persist(config);
    },

    exportTheme: (): ThemeExport => {
      const state = get();
      return {
        lightOverrides: state.lightOverrides,
        darkOverrides: state.darkOverrides,
        logoDataUrl: state.logoDataUrl,
      };
    },

    setLogo: (dataUrl: string | null): void => {
      set({ logoDataUrl: dataUrl });
      persist({ ...get(), logoDataUrl: dataUrl });
    },
  }));
